import { type CommonTemplateProps } from '#app/components/Modals/V2/GlobalModalSystem/types.js';
import { ModalTemplateWrapper } from '../ModalTemplateWrapper/ModalTemplateWrapper.js';
import { useMemo } from 'react';

type Props = CommonTemplateProps & {
  readonly applicantFirstName: string;
  readonly expertProgramName: string;
  readonly onConfirm: () => void;
};

const useMarkApplicantAsExpertModalText = ({
  applicantFirstName,
  expertProgramName,
}: {
  applicantFirstName: string;
  expertProgramName: string;
}) => {
  const text = useMemo(() => {
    if (!expertProgramName.trim()) {
      return {
        headerText: `Confirm ${applicantFirstName} as an expert`,
        primaryCTALabel: 'Confirm',
        secondaryCTALabel: 'Cancel',
        subHeaderText: `By confirming, they will be notified and given an expert badge, as well as added to your Expert Network.`,
      };
    }

    const firstLetter = expertProgramName.charAt(0).toLowerCase();
    // if it's a vowel, use 'an', otherwise use 'a'
    const article = 'aeiou'.includes(firstLetter) ? 'an' : 'a';

    let headingSuffix = `${article} ${expertProgramName} expert`;
    let subHeaderText = `By confirming, they will be notified and given an expert badge, as well as added to the ${expertProgramName} Expert Network.`;

    if (expertProgramName.endsWith('Experts')) {
      headingSuffix = `${article} ${expertProgramName.slice(0, -1)}`;
      subHeaderText = `By confirming, they will be notified and given an expert badge, as well as added to the ${expertProgramName} Network.`;
    }

    return {
      headerText: `Confirm ${applicantFirstName} as ${headingSuffix}`,
      primaryCTALabel: 'Confirm',
      secondaryCTALabel: 'Cancel',
      subHeaderText,
    };
  }, [applicantFirstName, expertProgramName]);

  return text;
};

export const MarkApplicantAsExpertModal = (props: Props) => {
  const { closeModal, isOpen, onConfirm } = props;

  const { headerText, primaryCTALabel, secondaryCTALabel, subHeaderText } =
    useMarkApplicantAsExpertModalText(props);

  return (
    <ModalTemplateWrapper
      ariaLabel="Confirm applicant as a expert"
      ctaOptions={{
        primaryCTA: {
          handleOnClick: onConfirm,
          label: primaryCTALabel,
        },
        secondaryCTA: {
          handleOnClick: closeModal,
          label: secondaryCTALabel,
        },
      }}
      desktopModalSize="md"
      headerText={headerText}
      isOpen={isOpen}
      onClose={closeModal}
      subheaderText={subHeaderText}
    />
  );
};
